import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  Target,
  Trophy,
  Check,
  Trash2,
  Calendar,
  Sparkles,
  Heart,
  Wallet,
  Brain,
  Users,
  Dumbbell,
  Briefcase,
  MessageCircle,
  BookOpen,
  Star,
  Lightbulb,
  Loader2,
  Map,
  ChevronRight,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const TIERS = [
  { name: "Beginner", threshold: 0, color: "bg-stone-400" },
  { name: "Bronze", threshold: 3, color: "bg-amber-600" },
  { name: "Silver", threshold: 7, color: "bg-slate-400" },
  { name: "Gold", threshold: 15, color: "bg-yellow-500" },
  { name: "Champion", threshold: 25, color: "bg-gradient-to-r from-primary to-destructive" },
];

const TIER_STYLES = {
  Beginner: "tier-beginner",
  Bronze: "tier-bronze",
  Silver: "tier-silver",
  Gold: "tier-gold",
  Champion: "tier-champion",
};

const CATEGORIES = [
  { id: "life", label: "Life", icon: Star, color: "text-primary" },
  { id: "financial", label: "Financial", icon: Wallet, color: "text-green-600" },
  { id: "spiritual", label: "Spiritual", icon: Heart, color: "text-purple-600" },
  { id: "family", label: "Family", icon: Users, color: "text-pink-600" },
  { id: "father", label: "Father", icon: Users, color: "text-blue-600" },
  { id: "mother", label: "Mother", icon: Heart, color: "text-rose-600" },
  { id: "health", label: "Health", icon: Dumbbell, color: "text-red-500" },
  { id: "career", label: "Career", icon: Briefcase, color: "text-indigo-600" },
  { id: "social", label: "Social", icon: MessageCircle, color: "text-cyan-600" },
  { id: "learning", label: "Learning", icon: BookOpen, color: "text-amber-600" },
];

const getCategoryIcon = (categoryId) => {
  const cat = CATEGORIES.find((c) => c.id === categoryId);
  return cat ? cat.icon : Target;
};

const getCategoryColor = (categoryId) => {
  const cat = CATEGORIES.find((c) => c.id === categoryId);
  return cat ? cat.color : "text-muted-foreground";
};

const getCategoryLabel = (categoryId) => {
  const cat = CATEGORIES.find((c) => c.id === categoryId);
  return cat ? cat.label : "Life";
};

export default function Goals() {
  const { user, updateUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("life");
  const [activeTab, setActiveTab] = useState("active");
  const [newGoal, setNewGoal] = useState({
    title: "",
    description: "",
    target_date: "",
    category: "life",
  });
  
  // Goal Structure Generator State
  const [goalStructure, setGoalStructure] = useState(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [structureGoalId, setStructureGoalId] = useState(null);

  useEffect(() => {
    fetchGoals();
    fetchSuggestions();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/goals/suggestions`);
      setSuggestions(response.data);
    } catch (error) {
      console.error("Failed to load suggestions");
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/goals`, newGoal);
      setGoals((prev) => [response.data, ...prev]);
      setCreateOpen(false);
      setNewGoal({ title: "", description: "", target_date: "", category: "life" });
      toast.success("Goal created! You've got this! 💪");
    } catch (error) {
      toast.error("Failed to create goal");
    }
  };

  const handleAddSuggestedGoal = async (suggestion, category) => {
    try {
      const response = await axios.post(`${API}/goals`, {
        title: suggestion.title,
        description: suggestion.description,
        category: category,
      });
      setGoals((prev) => [response.data, ...prev]);
      toast.success("Goal added! Let's make it happen! 🌟");
    } catch (error) {
      toast.error("Failed to add goal");
    }
  };

  const handleCompleteGoal = async (goalId) => {
    try {
      const response = await axios.put(`${API}/goals/${goalId}/complete`);
      setGoals((prev) =>
        prev.map((g) => (g.id === goalId ? { ...g, completed: true } : g))
      );
      updateUser({ trophies: response.data.trophies, tier: response.data.tier });
      toast.success(
        `Goal completed! 🏆 You now have ${response.data.trophies} trophies!`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete goal");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      toast.success("Goal removed");
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const generateGoalStructure = async (goal) => {
    setLoadingStructure(true);
    setStructureGoalId(goal.id);
    try {
      const response = await axios.post(`${API}/goals/generate-structure`, {
        goal_title: goal.title,
        goal_description: goal.description,
        target_date: goal.target_date,
        category: goal.category,
      });
      setGoalStructure(response.data);
      toast.success("Goal roadmap generated!");
    } catch (error) {
      toast.error("Failed to generate goal structure");
    } finally {
      setLoadingStructure(false);
    }
  };

  const completedGoals = goals.filter((g) => g.completed).length;
  const activeGoals = goals.filter((g) => !g.completed);
  const completedGoalsList = goals.filter((g) => g.completed);

  // Group active goals by category
  const goalsByCategory = activeGoals.reduce((acc, goal) => {
    const cat = goal.category || "life";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(goal);
    return acc;
  }, {});

  const currentTierIndex = TIERS.findIndex((t) => t.name === user?.tier);
  const nextTier = TIERS[currentTierIndex + 1];
  const progressToNextTier = nextTier
    ? ((completedGoals - TIERS[currentTierIndex].threshold) /
        (nextTier.threshold - TIERS[currentTierIndex].threshold)) *
      100
    : 100;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header with progress */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1633986636588-7da3d6595a12?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Your Goals
            </h1>
            <p className="text-muted-foreground text-lg">
              Track your journey and earn trophies as you grow
            </p>
          </div>

          {/* Trophy card */}
          <div className="trophy-card text-center">
            <div className="relative z-10">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-white/90" />
              <div className="text-6xl font-bold mb-2">{user?.trophies || 0}</div>
              <p className="text-white/80 text-lg mb-4">Trophies Earned</p>
              <Badge
                data-testid="goals-tier-badge"
                className={`${TIER_STYLES[user?.tier || "Beginner"]} px-4 py-2 text-lg`}
              >
                {user?.tier || "Beginner"}
              </Badge>
            </div>
          </div>

          {/* Progress to next tier */}
          {nextTier && (
            <div className="mt-6 card-happy">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">
                  Progress to {nextTier.name}
                </span>
                <span className="font-medium">
                  {completedGoals} / {nextTier.threshold} goals
                </span>
              </div>
              <Progress value={progressToNextTier} className="h-3" />
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tier roadmap */}
        <div className="card-happy mb-8">
          <h2 className="text-lg font-semibold mb-4">Tier Roadmap</h2>
          <div className="flex items-center justify-between overflow-x-auto gap-2 pb-2">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`flex flex-col items-center min-w-[80px] ${
                  user?.tier === tier.name ? "scale-110" : "opacity-60"
                } transition-all`}
              >
                <div
                  className={`w-12 h-12 rounded-full ${tier.color} flex items-center justify-center mb-2`}
                >
                  {completedGoals >= tier.threshold ? (
                    <Check className="w-6 h-6 text-white" />
                  ) : (
                    <span className="text-white font-bold">{tier.threshold}</span>
                  )}
                </div>
                <span className="text-xs font-medium">{tier.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-goal-btn" className="btn-primary">
                <Plus className="w-5 h-5 mr-2" />
                Create Custom Goal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateGoal} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="goal-category">Category</Label>
                  <Select
                    value={newGoal.category}
                    onValueChange={(value) =>
                      setNewGoal((prev) => ({ ...prev, category: value }))
                    }
                  >
                    <SelectTrigger data-testid="goal-category-select" className="input-happy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        return (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${cat.color}`} />
                              <span>{cat.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal-title">Goal Title</Label>
                  <Input
                    id="goal-title"
                    data-testid="goal-title-input"
                    placeholder="e.g., Run a marathon"
                    value={newGoal.title}
                    onChange={(e) =>
                      setNewGoal((prev) => ({ ...prev, title: e.target.value }))
                    }
                    required
                    className="input-happy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal-description">Description (optional)</Label>
                  <Textarea
                    id="goal-description"
                    data-testid="goal-description-input"
                    placeholder="Why is this goal important to you?"
                    value={newGoal.description}
                    onChange={(e) =>
                      setNewGoal((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="input-happy min-h-[80px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal-date">Target Date (optional)</Label>
                  <Input
                    id="goal-date"
                    type="date"
                    data-testid="goal-date-input"
                    value={newGoal.target_date}
                    onChange={(e) =>
                      setNewGoal((prev) => ({
                        ...prev,
                        target_date: e.target.value,
                      }))
                    }
                    className="input-happy"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="save-goal-btn"
                  className="w-full btn-primary"
                >
                  Create Goal
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="browse-suggestions-btn"
                variant="outline"
                className="rounded-full"
              >
                <Lightbulb className="w-5 h-5 mr-2 text-primary" />
                Browse Goal Ideas
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Goal Ideas by Category
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Category selector */}
                <ScrollArea className="w-full whitespace-nowrap">
                  <div className="flex gap-2 pb-2">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                            selectedCategory === cat.id
                              ? "bg-primary text-white"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* Suggestions list */}
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {suggestions[selectedCategory]?.map((suggestion, index) => (
                      <div
                        key={index}
                        className="card-happy flex items-center justify-between gap-4"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold">{suggestion.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {suggestion.description}
                          </p>
                        </div>
                        <Button
                          data-testid={`add-suggestion-${index}`}
                          onClick={() => {
                            handleAddSuggestedGoal(suggestion, selectedCategory);
                          }}
                          size="sm"
                          className="btn-primary shrink-0"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for Active/Completed */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-xs">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Active ({activeGoals.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Done ({completedGoalsList.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card-happy animate-pulse h-24"></div>
                ))}
              </div>
            ) : activeGoals.length === 0 ? (
              <div className="card-happy text-center py-12">
                <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No active goals</h3>
                <p className="text-muted-foreground mb-6">
                  Set a goal and start earning trophies!
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    data-testid="empty-create-goal-btn"
                    onClick={() => setCreateOpen(true)}
                    className="btn-primary"
                  >
                    Create Custom Goal
                  </Button>
                  <Button
                    onClick={() => setSuggestionsOpen(true)}
                    variant="outline"
                    className="rounded-full"
                  >
                    <Lightbulb className="w-5 h-5 mr-2 text-primary" />
                    Browse Ideas
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-8" data-testid="active-goals-list">
                {/* Goals grouped by category */}
                {CATEGORIES.map((cat) => {
                  const categoryGoals = goalsByCategory[cat.id];
                  if (!categoryGoals || categoryGoals.length === 0) return null;

                  const Icon = cat.icon;
                  return (
                    <div key={cat.id}>
                      <div className="flex items-center gap-2 mb-4">
                        <Icon className={`w-5 h-5 ${cat.color}`} />
                        <h3 className="font-semibold text-lg">{cat.label} Goals</h3>
                        <Badge variant="secondary" className="ml-auto">
                          {categoryGoals.length}
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        {categoryGoals.map((goal) => (
                          <div
                            key={goal.id}
                            className="card-happy"
                            data-testid={`goal-card-${goal.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg">{goal.title}</h4>
                                {goal.description && (
                                  <p className="text-muted-foreground text-sm mt-1">
                                    {goal.description}
                                  </p>
                                )}
                                {goal.target_date && (
                                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                                    <Calendar className="w-4 h-4" />
                                    <span>
                                      Target: {format(new Date(goal.target_date), "PPP")}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  data-testid={`roadmap-goal-btn-${goal.id}`}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => generateGoalStructure(goal)}
                                  disabled={loadingStructure && structureGoalId === goal.id}
                                  className="rounded-full"
                                >
                                  {loadingStructure && structureGoalId === goal.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Map className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  data-testid={`complete-goal-btn-${goal.id}`}
                                  onClick={() => handleCompleteGoal(goal.id)}
                                  className="btn-primary"
                                >
                                  <Check className="w-5 h-5 mr-1" />
                                  Complete
                                </Button>
                                <Button
                                  data-testid={`delete-goal-btn-${goal.id}`}
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteGoal(goal.id)}
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Goal Structure/Roadmap Display */}
                            {goalStructure && structureGoalId === goal.id && (
                              <div className="mt-4 pt-4 border-t border-border space-y-4">
                                <div className="bg-primary/5 rounded-xl p-4">
                                  <h5 className="font-semibold mb-2 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-primary" />
                                    Your Roadmap
                                  </h5>
                                  <p className="text-sm text-muted-foreground">{goalStructure.overview}</p>
                                </div>
                                
                                {/* Milestones */}
                                {goalStructure.milestones?.length > 0 && (
                                  <div>
                                    <h5 className="font-medium text-sm mb-2">Milestones</h5>
                                    <div className="space-y-2">
                                      {goalStructure.milestones.map((milestone, i) => (
                                        <div key={i} className="flex gap-3 text-sm">
                                          <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs">
                                            {i + 1}
                                          </div>
                                          <div>
                                            <span className="font-medium">{milestone.title}</span>
                                            <span className="text-muted-foreground ml-2">({milestone.timeframe})</span>
                                            {milestone.description && (
                                              <p className="text-muted-foreground">{milestone.description}</p>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Daily Actions */}
                                {goalStructure.daily_actions?.length > 0 && (
                                  <div>
                                    <h5 className="font-medium text-sm mb-2">Daily Actions</h5>
                                    <ul className="space-y-1">
                                      {goalStructure.daily_actions.map((action, i) => (
                                        <li key={i} className="flex gap-2 text-sm">
                                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                          {action}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Motivation Tips */}
                                {goalStructure.motivation_tips?.length > 0 && (
                                  <div className="bg-amber-50 rounded-xl p-4">
                                    <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <Star className="w-4 h-4 text-amber-500" />
                                      Stay Motivated
                                    </h5>
                                    <ul className="space-y-1">
                                      {goalStructure.motivation_tips.map((tip, i) => (
                                        <li key={i} className="text-sm text-amber-800">{tip}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setGoalStructure(null); setStructureGoalId(null); }}
                                  className="text-muted-foreground"
                                >
                                  Close Roadmap
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedGoalsList.length === 0 ? (
              <div className="card-happy text-center py-12">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No completed goals yet</h3>
                <p className="text-muted-foreground">
                  Complete your first goal to earn a trophy!
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="completed-goals-list">
                {completedGoalsList.map((goal) => {
                  const Icon = getCategoryIcon(goal.category);
                  return (
                    <div
                      key={goal.id}
                      className="card-happy opacity-80 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium line-through text-muted-foreground">
                              {goal.title}
                            </h4>
                            <Badge variant="outline" className="text-xs">
                              <Icon className={`w-3 h-3 mr-1 ${getCategoryColor(goal.category)}`} />
                              {getCategoryLabel(goal.category)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Completed • +1 Trophy 🏆
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}
